'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Typography from '@alltrails/core/components/Typography';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import NullState from '@alltrails/denali/components/NullState';
import Tent from '@alltrails/denali/illustrations/Tent';
import { useLazyGetListTrailsQuery } from '@alltrails/explore-service/redux/exploreServiceApi';
import { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import styles from './styles.module.scss';
import TrailSegmentsLoading from './TrailSegmentsLoading';

const TrailSegments = dynamic(() => import('./TrailSegments'), { ssr: false });

type TrailSegmentsSectionProps = {
  isAdminOrPLPUser?: boolean;
  segmentIds: number[];
};

// TODO: Translate (DISCO-24081)
const TrailSegmentsSection = ({ isAdminOrPLPUser, segmentIds }: TrailSegmentsSectionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasLoaded = useRef(false);
  const isVisible = useIsVisible(containerRef);

  const [getTrails, { data, isError, isFetching, isLoading, isUninitialized }] = useLazyGetListTrailsQuery();

  const fetchTrails = useCallback(() => {
    getTrails({
      additionalAttributesToRetrieve: ['polyline'],
      filters: { recordIds: [{ ids: segmentIds, mode: 'and', recordType: 'trail' }] },
      location: { center: { lat: 0, lng: 0 }, maxRadius: Number.MAX_SAFE_INTEGER }
    });
  }, [getTrails, segmentIds]);

  useEffect(() => {
    if (isVisible && !hasLoaded.current) {
      hasLoaded.current = true;
      fetchTrails();
    }
  }, [fetchTrails, isVisible]);

  const content = useMemo(() => {
    if (isError) {
      return (
        <div className={styles.nullStateContainer}>
          <NullState
            illustration={{ Component: Tent }}
            title="Let’s try that again"
            description="We’re having trouble loading route segments"
            buttons={{
              default: {
                onClick: fetchTrails,
                text: 'Retry'
              }
            }}
            noTopMargin
          />
        </div>
      );
    }

    if (isFetching || isLoading || isUninitialized) {
      return <TrailSegmentsLoading />;
    }

    if (data) {
      const sortedTrails = segmentIds.map(trailId => data.searchResults.find(trail => trail.ID === trailId)).filter(Boolean) as TrailSearchResult[];
      return <TrailSegments trails={sortedTrails} />;
    }

    return null;
  }, [data, fetchTrails, isError, isFetching, isLoading, isUninitialized, segmentIds]);

  return content ? (
    <div className={classNames(styles.container, { [styles.admin]: isAdminOrPLPUser })} ref={containerRef}>
      {/* TODO: Translate (DISCO-24081) */}
      <Typography variant="heading400">Route segments</Typography>
      {content}
    </div>
  ) : null;
};

export default TrailSegmentsSection;
